import React, { useEffect, useMemo, useRef, useState } from "react";

type Step = {
  key: string;
  title: string;
  subtitle: string;
  media: {
    type: "video" | "image";
    src: string;
    poster?: string;
  };
};

declare namespace JSX {
  interface IntrinsicElements {
    "stripe-pricing-table": React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLElement>,
      HTMLElement
    > & {
      "pricing-table-id": string;
      "publishable-key": string;
    };
  }
}

/** Detect system dark mode and react to changes */
function usePrefersDarkMode() {
  const get = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [isDark, setIsDark] = useState<boolean>(get() || false);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    // For older Safari
    // @ts-ignore
    mq.addEventListener
      ? mq.addEventListener("change", handler)
      : mq.addListener(handler);
    return () => {
      // @ts-ignore
      mq.removeEventListener
        ? mq.removeEventListener("change", handler)
        : mq.removeListener(handler);
    };
  }, []);

  return isDark;
}

/** Build style tokens based on current color scheme */
function getTokens(isDark: boolean) {
  if (isDark) {
    return {
      bgPage:
        "radial-gradient(1200px 600px at 20% 10%, rgba(42,168,128,0.15), transparent), radial-gradient(1200px 600px at 80% 90%, rgba(18,115,81,0.15), transparent), #0c0f10",
      fg: "#e8f6f1",
      fgSubtle: "#cdebe1",
      cardBg:
        "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
      cardBorder: "1px solid rgba(255,255,255,0.08)",
      mediaBg:
        "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
      mediaBorder: "1px solid rgba(255,255,255,0.08)",
      primary1: "#2aa880",
      primary2: "#127351",
      primaryBorder: "#1a5e4b",
      pillBorder: "1px solid rgba(255,255,255,0.14)",
      pillBg: "rgba(255,255,255,0.04)",
      pillActiveBg:
        "linear-gradient(180deg, rgba(42,168,128,0.35), rgba(18,115,81,0.25))",
      mutedBarBg: "rgba(255,255,255,0.1)",
      secondaryBtnBorder: "1px solid rgba(255,255,255,0.18)",
      secondaryBtnBg: "rgba(255,255,255,0.06)",
      titleShadow: "0 1px 2px rgba(0,0,0,0.2)",
      mediaBackdrop: "transparent",
    };
  }
  // Light mode
  return {
    bgPage:
      "radial-gradient(1200px 600px at 20% 10%, rgba(42,168,128,0.08), transparent), radial-gradient(1200px 600px at 80% 90%, rgba(18,115,81,0.08), transparent), #f7faf9",
    fg: "#0d1b17",
    fgSubtle: "#244a3f",
    cardBg: "linear-gradient(180deg, rgba(0,0,0,0.03), rgba(0,0,0,0.015))",
    cardBorder: "1px solid rgba(0,0,0,0.06)",
    mediaBg: "linear-gradient(180deg, rgba(0,0,0,0.03), rgba(0,0,0,0.015))",
    mediaBorder: "1px solid rgba(0,0,0,0.06)",
    primary1: "#2aa880",
    primary2: "#127351",
    primaryBorder: "#187059",
    pillBorder: "1px solid rgba(0,0,0,0.12)",
    pillBg: "rgba(0,0,0,0.03)",
    pillActiveBg:
      "linear-gradient(180deg, rgba(42,168,128,0.22), rgba(18,115,81,0.14))",
    mutedBarBg: "rgba(0,0,0,0.08)",
    secondaryBtnBorder: "1px solid rgba(0,0,0,0.12)",
    secondaryBtnBg: "rgba(0,0,0,0.04)",
    titleShadow: "none",
    mediaBackdrop: "transparent",
  };
}

/** URL-encode file names with spaces safely */
const enc = (p: string) => encodeURI(p);

const STEPS: Step[] = [
  {
    key: "download",
    title: "Download the app",
    subtitle: "1. This is the same app your clients will install.",
    media: { type: "video", src: enc("/media/onboarding/step1.mp4") },
  },
  {
    key: "create-account",
    title: "Remember your credentials",
    subtitle:
      "2. Let’s create your account — this is also what your clients will go through.",
    media: { type: "video", src: enc("/media/onboarding/step2.mp4") },
  },
  {
    key: "skip-paywall",
    title: "Press 'Continue to home screen'",
    subtitle: "3. Let’s skip the paywall.",
    media: { type: "video", src: enc("/media/onboarding/step3.mp4") },
  },
  {
    key: "coach-login",
    title: "Access the coaching platform",
    subtitle: "4. Log into Coaching Platform",
    media: { type: "video", src: enc("/media/onboarding/step4.mp4") },
  },
  {
    key: "done",
    title: "Done — congrats! We'll email you within 24 hours",
    subtitle: "5. All set! Choose a plan to continue.",
    media: { type: "video", src: enc("/media/onboarding/step5.mp4") },
  },
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function Onboarding() {
  const [stepIndex, setStepIndex] = useState(0);
  const [aspect, setAspect] = useState<string | null>(null); // dynamic aspect ratio "w / h"
  const isMobile = useIsMobile(900);
  const isDark = usePrefersDarkMode();
  const T = getTokens(isDark);

  const step = useMemo(() => STEPS[stepIndex], [stepIndex]);
  const total = STEPS.length;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Load Stripe script dynamically when component mounts
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/pricing-table.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const goNext = () => {
    if (!isLast) setStepIndex((i) => Math.min(i + 1, total - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset aspect ratio when step changes so the new media can define it
  useEffect(() => {
    setAspect(null);
  }, [stepIndex]);

  // Autoplay when the user goes to the next step (or any step change where media is video)
  useEffect(() => {
    if (isLast) return; // no video on last if you swap to pricing table
    const v = videoRef.current;
    if (!v) return;

    // iOS/Safari requires muted + playsInline for autoplay
    v.muted = true;

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          const onLoaded = () => {
            v.play().catch(() => {});
            v.removeEventListener("loadeddata", onLoaded);
          };
          v.addEventListener("loadeddata", onLoaded);
        });
      }
    };

    const t = setTimeout(tryPlay, 0);
    return () => clearTimeout(t);
  }, [stepIndex, isLast]);

  // When metadata loads, set aspect ratio from intrinsic media to prevent any cropping
  const onVideoMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    const w = v.videoWidth || 9;
    const h = v.videoHeight || 16;
    setAspect(`${w} / ${h}`);
  };

  const onImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth || 16;
    const h = img.naturalHeight || 9;
    setAspect(`${w} / ${h}`);
  };

  const styles = getStyles(T, aspect);

  return (
    <div style={styles.shell}>
      <div
        style={{
          ...styles.container,
          flexDirection: isMobile ? "column" : "row",
          alignItems: "stretch",
        }}
      >
        {/* Left Pane: Text + Actions */}
        <section
          style={{
            ...styles.leftPane,
            width: isMobile ? "100%" : "48%",
            minHeight: isMobile ? "min(40vh, 520px)" : undefined,
          }}
        >
          <div style={styles.preface}>{step.subtitle}</div>
          <h1 style={styles.title}>{step.title}</h1>

          {/* Progress */}
          <div style={styles.progressWrap} aria-label="Progress">
            <div style={styles.progressBarBg}>
              <div
                style={{
                  ...styles.progressBarFill,
                  width: `${((stepIndex + 1) / total) * 100}%`,
                }}
              />
            </div>
            <div style={styles.progressMeta}>
              Step {stepIndex + 1} of {total}
            </div>
          </div>

          {/* Nav Buttons */}
          <div style={styles.actions}>
            <button
              onClick={goBack}
              disabled={isFirst}
              style={{
                ...styles.buttonSecondary,
                ...(isFirst ? styles.buttonDisabled : {}),
              }}
            >
              Back
            </button>
            <button
              onClick={goNext}
              disabled={isLast}
              style={{
                ...styles.buttonPrimary,
                ...(isLast ? styles.buttonDisabled : {}),
              }}
            >
              {isLast ? "Finished" : "Next"}
            </button>
          </div>
        </section>

        {/* Right Pane: Media or Pricing Table */}
        <section
          style={{
            ...styles.rightPane,
            width: isMobile ? "100%" : "52%",
            minHeight: isMobile ? "min(40vh, 480px)" : undefined,
          }}
        >
          <div style={styles.mediaFrame}>
            {isLast ? (
              React.createElement("stripe-pricing-table", {
                "pricing-table-id": "prctbl_1SPbenIqFV1p4tf9S6WdhtzA",
                "publishable-key":
                  "pk_live_51S9A8EIqFV1p4tf9TPYzIv3181pJ2cISKwUVTGEhEstGgCM39Bi1f9fm3SIoIUDm4yRl4ihKt3sQ03zFQUpMlZTw002WLaGbND",
              })
            ) : step.media.type === "video" ? (
              <video
                key={step.media.src}
                ref={videoRef}
                style={styles.video}
                controls // ✅ controls back
                playsInline
                muted
                autoPlay
                preload="metadata"
                poster={step.media.poster}
                onLoadedMetadata={onVideoMeta}
              >
                <source src={step.media.src} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                ref={imgRef}
                src={step.media.src}
                alt={step.title}
                style={styles.image}
                onLoad={onImageLoad}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function getStyles(
  T: ReturnType<typeof getTokens>,
  aspect: string | null
): Record<string, React.CSSProperties> {
  return {
    shell: {
      width: "100vw",
      minHeight: "100dvh",
      background: T.bgPage,
      color: T.fg,
      overflowX: "hidden",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      boxSizing: "border-box",
      padding: 16,
      paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
      transition: "background-color 200ms ease, color 200ms ease",
    },
    container: {
      display: "flex",
      justifyContent: "space-around",
      gap: 20,
      width: "100%",
      minHeight: "calc(100dvh - 32px)",
      padding: "60px 8px 80px",
      boxSizing: "border-box",
    },
    leftPane: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 18,
      borderRadius: 16,
      padding: 20,
      background: T.cardBg,
      border: T.cardBorder,
      boxShadow: "0 10px 30px rgba(0,0,0,0.20)",
      backdropFilter: "blur(10px)",
    },
    preface: {
      fontSize: 14,
      letterSpacing: 0.3,
      color: T.fgSubtle,
      opacity: 0.95,
    },
    title: {
      fontSize: 26,
      lineHeight: 1.25,
      margin: 0,
      color: T.fg,
      textShadow: T.titleShadow,
    },
    progressWrap: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    progressBarBg: {
      flex: 1,
      height: 8,
      background: T.mutedBarBg,
      borderRadius: 999,
      overflow: "hidden",
    },
    progressBarFill: {
      height: "100%",
      background: `linear-gradient(90deg, ${T.primary1} 0%, ${T.primary2} 100%)`,
    },
    progressMeta: {
      fontSize: 12,
      color: T.fgSubtle,
      minWidth: 88,
      textAlign: "right",
    },
    actions: {
      display: "flex",
      gap: 10,
      marginTop: 4,
      flexWrap: "wrap",
    },
    buttonPrimary: {
      appearance: "none",
      border: `1px solid ${T.primaryBorder}`,
      padding: "10px 16px",
      borderRadius: 10,
      background: `linear-gradient(180deg, ${T.primary1}, ${T.primary2})`,
      color: "#fff",
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 6px 18px rgba(42,168,128,0.25)",
      transition:
        "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
    },
    buttonSecondary: {
      appearance: "none",
      border: T.secondaryBtnBorder,
      padding: "10px 16px",
      borderRadius: 10,
      background: T.secondaryBtnBg,
      color: T.fg,
      fontWeight: 600,
      cursor: "pointer",
      transition: "transform 120ms ease, opacity 120ms ease",
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
      boxShadow: "none",
    },
    rightPane: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 16,
    },
    mediaFrame: {
      position: "relative",
      // Make letterbox fully transparent so the page background shows through:
      background: "transparent",
      border: "none",
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: "none",
      backdropFilter: "none",
      width: "100%",
      // Use the media’s intrinsic aspect so it never crops:
      aspectRatio: aspect || undefined, // e.g. "9 / 16" or "16 / 9"
      // Fallback minimums so layout is stable before metadata arrives
      minHeight: 360,
      maxHeight: "85vh",
      backgroundColor: "transparent",
    },
    video: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "contain", // prevents cropping and creates transparent letterbox
      objectPosition: "center center",
      WebkitTouchCallout: "none" as any,
      backgroundColor: "transparent",
    },
    image: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "contain",
      objectPosition: "center center",
      backgroundColor: "transparent",
    },
  };
}
